import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Grid,
  Alert,
  CircularProgress,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Textarea
} from '@mui/joy';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaEye, 
  FaDownload, 
  FaSync
} from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { emailActivity, type EmailActivityLog, type EmailStats } from '../lib/emailActivityService';
import { directSupabase } from '../lib/supabaseClient';
import { format } from 'date-fns';

const PAGE_SIZE = 20;

type ActivityRow = { kind: 'email'; id: string; created_at: string; log: EmailActivityLog };
type LinkMetaMap = Record<string, { transmissionUrl: string | null; documentId: string | null }>;

function getOpenMeta(log: EmailActivityLog): { openedAt?: string; openCount?: number; internalOpenCount?: number } {
  const meta: any = (log as any)?.metadata;
  if (!meta || typeof meta !== 'object') return {};
  const openedAt = typeof meta.opened_at === 'string' ? meta.opened_at : undefined;
  const openCount = typeof meta.open_count === 'number' ? meta.open_count : undefined;
  const internalOpenCount = typeof meta.internal_open_count === 'number' ? meta.internal_open_count : undefined;
  return { openedAt, openCount, internalOpenCount };
}

function getCustomerActionLabel(log: EmailActivityLog): string | null {
  const meta: any = (log as any)?.metadata;
  const action = String(meta?.customerAction?.action || meta?.customer_action?.action || '').trim().toLowerCase();
  if (!action) return null;
  if (action === 'accept' || action === 'accepted') return 'Customer accepted';
  if (action === 'reject' || action === 'rejected') return 'Customer rejected';
  if (action === 'query') return 'Customer query';
  if (action === 'opened' || action === 'open') return 'Customer opened';
  return 'Customer action';
}

function getCustomerActionDetails(log: EmailActivityLog): { action?: string; reason?: string; message?: string; at?: string } {
  const meta: any = (log as any)?.metadata;
  const ca = meta?.customerAction || meta?.customer_action || null;
  if (!ca || typeof ca !== 'object') return {};
  const action = typeof ca.action === 'string' ? ca.action : undefined;
  const reason = typeof ca.reason === 'string' ? ca.reason : undefined;
  const message = typeof ca.message === 'string' ? ca.message : undefined;
  const at = typeof ca.at === 'string' ? ca.at : undefined;
  return { action, reason, message, at };
}

function getCustomerActionType(log: EmailActivityLog): 'accept' | 'reject' | 'query' | 'opened' | '' {
  const details = getCustomerActionDetails(log);
  const action = String(details.action || '').trim().toLowerCase();
  if (!action) return '';
  if (action === 'accepted') return 'accept';
  if (action === 'rejected') return 'reject';
  if (action === 'open') return 'opened';
  if (action === 'accept' || action === 'reject' || action === 'query' || action === 'opened') return action;
  return '';
}

function getTransmissionAndDocumentLinks(log: EmailActivityLog, linkMetaMap: LinkMetaMap): { transmissionUrl: string | null; documentUrl: string | null } {
  const meta: any = (log as any)?.metadata;
  if (!meta || typeof meta !== 'object') {
    return { transmissionUrl: null, documentUrl: null };
  }
  const shareLinkId = String(meta.shareLinkId || '').trim();
  const transmissionUrlRaw = String(
    meta.shareUrl ||
    meta.secureUrl ||
    ''
  ).trim();
  const mapped = shareLinkId ? linkMetaMap[shareLinkId] : null;
  const transmissionUrl = transmissionUrlRaw || mapped?.transmissionUrl || null;

  const documentId = String(
    meta.documentId ||
    meta.document_id ||
    meta.docId ||
    ''
  ).trim() || mapped?.documentId || null;
  return {
    transmissionUrl,
    documentUrl: documentId ? `/docs/${encodeURIComponent(documentId)}` : null,
  };
}

function matchesCustomerActionFilter(log: EmailActivityLog, filterValue: string): boolean {
  const actionType = getCustomerActionType(log);
  if (!filterValue) return true;
  if (filterValue === 'none') return actionType === '';
  return actionType === filterValue;
}

type EmailActivityError = {
  message: string;
};

const EmailActivityDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<EmailActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<EmailActivityError | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [selectedLog, setSelectedLog] = useState<EmailActivityLog | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [linkMetaMap, setLinkMetaMap] = useState<LinkMetaMap>({});
  const [filters, setFilters] = useState<{
    templateType: string;
    recipient: string;
    status: string;
    customerAction: string;
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({
    templateType: '',
    recipient: '',
    status: '',
    customerAction: '',
    startDate: undefined,
    endDate: undefined
  });

  // Load logs and stats
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const hasCustomerActionFilter = !!String(filters.customerAction || '').trim();
      const requestFilters = hasCustomerActionFilter
        ? { ...filters, customerAction: '' } // we'll apply exact action filtering client-side to avoid JSON-path quirks
        : filters;

      const [logsResult, statsResult] = await Promise.all([
        hasCustomerActionFilter
          ? emailActivity.getRecent(1, 1000, requestFilters)
          : emailActivity.getRecent(page, PAGE_SIZE, requestFilters),
        emailActivity.getStats(),
      ]);

      if (logsResult.error) throw new Error(logsResult.error);
      if (statsResult.error) throw new Error(statsResult.error);

      if (hasCustomerActionFilter) {
        const allRows = Array.isArray(logsResult.data) ? logsResult.data : [];
        const filteredRows = allRows.filter((log) => matchesCustomerActionFilter(log, String(filters.customerAction || '').trim()));
        const from = (page - 1) * PAGE_SIZE;
        const to = from + PAGE_SIZE;
        setLogs(filteredRows.slice(from, to));
        setTotalCount(filteredRows.length);
      } else {
        setLogs(logsResult.data);
        setTotalCount(logsResult.count || 0);
      }
      setStats(statsResult.data);
    } catch (err) {
      setError({
        message: err instanceof Error ? err.message : 'Failed to load email activity'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, filters]);

  const handlePreview = (log: EmailActivityLog) => {
    setSelectedLog(log);
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
    const params = new URLSearchParams(location.search);
    if (params.has('preview')) {
      params.delete('preview');
      const next = params.toString();
      navigate(`/admin${next ? `?${next}` : ''}`, { replace: true });
    }
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export functionality to be implemented');
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      templateType: '',
      recipient: '',
      status: '',
      customerAction: '',
      startDate: undefined,
      endDate: undefined
    });
    setPage(1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'danger';
      case 'draft':
        return 'warning';
      default:
        return 'primary'; // Use 'primary' instead of 'info'
    }
  };

  const getStatusColorForLog = (log: EmailActivityLog) => {
    const s = String(log.status || '').trim().toLowerCase();
    if (s === 'received') {
      const details = getCustomerActionDetails(log);
      const action = String(details.action || '').trim().toLowerCase();
      if (action === 'reject' || action === 'rejected') return 'danger';
      if (action === 'accept' || action === 'accepted') return 'success';
      if (action === 'query') return 'warning';
      if (action === 'opened' || action === 'open') return 'primary';
      return 'primary';
    }
    return getStatusColor(s);
  };

  const getStatusLabel = (log: EmailActivityLog) => {
    const s = String(log.status || '').trim().toLowerCase();
    if (s === 'received') {
      // These rows are customer actions (accept/reject/query), not email opens.
      return getCustomerActionLabel(log) || 'Customer action';
    }
    return String(log.status || '').trim() || 'unknown';
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const activityRows = useMemo<ActivityRow[]>(() => {
    return logs
      .map<ActivityRow>((log) => ({
        kind: 'email',
        id: log.id,
        created_at: log.created_at,
        log,
      }))
      .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  }, [logs]);

  const templateTypeOptions = useMemo(() => {
    const fromStats = Object.keys(stats?.byTemplateType || {});
    const fromLogs = Array.from(new Set(logs.map((log) => String(log.template_type || '').trim()).filter(Boolean)));
    return Array.from(new Set([...fromStats, ...fromLogs])).sort((a, b) => a.localeCompare(b));
  }, [stats, logs]);

  const recipientOptions = useMemo(() => {
    return Array.from(new Set(logs.map((log) => String(log.recipient || '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b));
  }, [logs]);

  const previewFromQueryId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get('preview') || '').trim();
  }, [location.search]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!previewFromQueryId) return;
      const fromCurrentPage = logs.find((log) => String(log.id) === previewFromQueryId);
      if (fromCurrentPage) {
        if (!alive) return;
        setSelectedLog(fromCurrentPage);
        setShowPreview(true);
        return;
      }
      const { data, error } = await emailActivity.getById(previewFromQueryId);
      if (!alive) return;
      if (!error && data) {
        setSelectedLog(data);
        setShowPreview(true);
      }
    })();
    return () => { alive = false; };
  }, [previewFromQueryId, logs]);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Build best-effort link map from currently loaded rows first.
      const baseMap: LinkMetaMap = {};
      const ids = new Set<string>();
      logs.forEach((log) => {
        const meta: any = (log as any)?.metadata;
        const shareLinkId = String(meta?.shareLinkId || '').trim();
        if (!shareLinkId) return;
        ids.add(shareLinkId);
        const transmissionUrl = String(meta?.shareUrl || meta?.secureUrl || '').trim() || null;
        const documentId = String(meta?.documentId || meta?.document_id || meta?.docId || '').trim() || null;
        if (!baseMap[shareLinkId]) {
          baseMap[shareLinkId] = { transmissionUrl, documentId };
        } else {
          baseMap[shareLinkId] = {
            transmissionUrl: baseMap[shareLinkId].transmissionUrl || transmissionUrl,
            documentId: baseMap[shareLinkId].documentId || documentId,
          };
        }
      });

      const missingIds = Array.from(ids).filter((id) => {
        const row = baseMap[id];
        return !row || !row.transmissionUrl || !row.documentId;
      });

      if (missingIds.length > 0) {
        try {
          const orFilter = missingIds
            .map((id) => `metadata->>shareLinkId.eq.${id.replace(/,/g, '')}`)
            .join(',');
          const { data } = await directSupabase
            .from('email_activity_log')
            .select('metadata,created_at')
            .or(orFilter)
            .order('created_at', { ascending: false })
            .limit(500);

          (data || []).forEach((row: any) => {
            const meta: any = row?.metadata;
            const shareLinkId = String(meta?.shareLinkId || '').trim();
            if (!shareLinkId) return;
            const transmissionUrl = String(meta?.shareUrl || meta?.secureUrl || '').trim() || null;
            const documentId = String(meta?.documentId || meta?.document_id || meta?.docId || '').trim() || null;
            const prev = baseMap[shareLinkId] || { transmissionUrl: null, documentId: null };
            baseMap[shareLinkId] = {
              transmissionUrl: prev.transmissionUrl || transmissionUrl,
              documentId: prev.documentId || documentId,
            };
          });
        } catch (e) {
          console.warn('Failed to enrich transmission links for activity rows', e);
        }
      }

      if (!alive) return;
      setLinkMetaMap(baseMap);
    })();
    return () => { alive = false; };
  }, [logs]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography level="h3" mb={2}>Activity Monitor</Typography>
      
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="primary">
            <Typography level="title-md">Total Emails (7d)</Typography>
            <Typography level="h2">{stats?.last7Days || 0}</Typography>
          </Card>
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="success">
            <Typography level="title-md">Sent</Typography>
            <Typography level="h2">{stats?.byStatus?.sent || 0}</Typography>
          </Card>
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="danger">
            <Typography level="title-md">Failed</Typography>
            <Typography level="h2">{stats?.byStatus?.failed || 0}</Typography>
          </Card>
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <Card variant="soft" color="neutral">
            <Typography level="title-md">Drafts</Typography>
            <Typography level="h2">{stats?.byStatus?.draft || 0}</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography level="title-md">Filters</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="sm"
                variant="outlined"
                startDecorator={<IconWrapper icon={FaSync} />}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
              <Button
                size="sm"
                variant="outlined"
                startDecorator={<IconWrapper icon={FaDownload} />}
                onClick={handleExport}
              >
                Export
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
              <FormControl size="sm">
                <FormLabel>Template Type</FormLabel>
                <Select
                  value={filters.templateType || null}
                  onChange={(_, value) => handleFilterChange('templateType', value || '')}
                  placeholder="All templates"
                >
                  <Option value="">All templates</Option>
                  {templateTypeOptions.map((templateType) => (
                    <Option key={templateType} value={templateType}>
                      {templateType}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <FormControl size="sm">
                <FormLabel>Recipient</FormLabel>
                <Select
                  value={filters.recipient || null}
                  onChange={(_, value) => handleFilterChange('recipient', value || '')}
                  placeholder="All recipients"
                >
                  <Option value="">All recipients</Option>
                  {recipientOptions.map((recipient) => (
                    <Option key={recipient} value={recipient}>
                      {recipient}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <FormControl size="sm">
                <FormLabel>Delivery Status</FormLabel>
                <Select
                  value={filters.status || null}
                  onChange={(_, value) => handleFilterChange('status', value || '')}
                >
                  <Option value="">All statuses</Option>
                  <Option value="sent">Sent</Option>
                  <Option value="failed">Failed</Option>
                  <Option value="draft">Draft</Option>
                  <Option value="received">Received (customer action)</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <FormControl size="sm">
                <FormLabel>Customer Action</FormLabel>
                <Select
                  value={filters.customerAction || null}
                  onChange={(_, value) => handleFilterChange('customerAction', value || '')}
                  placeholder="All actions"
                >
                  <Option value="">All actions</Option>
                  <Option value="accept">Accepted quote</Option>
                  <Option value="reject">Rejected quote</Option>
                  <Option value="query">Customer query</Option>
                  <Option value="opened">Customer opened</Option>
                  <Option value="none">No customer action</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <Stack direction="row" spacing={1} alignItems="end" sx={{ height: '100%' }}>
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={clearFilters}
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error.message}
        </Alert>
      )}

      {/* Email Activity Table */}
      <Card variant="outlined">
        <Box sx={{ p: 2 }}>
          <Typography level="title-sm" sx={{ mb: 2, fontWeight: 'bold' }}>
            Activity
          </Typography>
          
          {activityRows.length === 0 ? (
            <Typography level="body-sm" color="neutral">
              No activity found
            </Typography>
          ) : (
            <Stack spacing={2}>
              {activityRows.map((row) => (
                <Card key={`${row.kind}-${row.id}`} variant="outlined" size="sm">
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid xs={12} sm={3}>
                        <Typography level="body-sm" fontWeight="bold">
                          {String(row.log.subject || '').trim() || row.log.template_type}
                        </Typography>
                      </Grid>
                      <Grid xs={12} sm={2}>
                        <Typography level="body-sm">
                          {row.log.recipient}
                        </Typography>
                      </Grid>
                      <Grid xs={12} sm={2}>
                        <Chip
                          size="sm"
                          color={getStatusColorForLog(row.log)}
                          variant="soft"
                        >
                          {getStatusLabel(row.log)}
                        </Chip>
                      </Grid>
                      <Grid xs={12} sm={2}>
                        <Typography level="body-sm">
                          {formatDate(row.log.created_at)}
                        </Typography>
                      </Grid>
                      <Grid xs={12} sm={3}>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                          sx={{ flexWrap: 'nowrap' }}
                        >
                          <Button
                            size="sm"
                            variant="outlined"
                            onClick={() => handlePreview(row.log)}
                            startDecorator={<IconWrapper icon={FaEye} />}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Preview
                          </Button>
                          {(() => {
                            const actionType = getCustomerActionType(row.log);
                            if (!['accept', 'reject', 'query'].includes(actionType)) return null;
                            const links = getTransmissionAndDocumentLinks(row.log, linkMetaMap);
                            return (
                              <>
                                {links.transmissionUrl && (
                                  <Button
                                    component="a"
                                    href={links.transmissionUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="sm"
                                    variant="soft"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    Public Doc
                                  </Button>
                                )}
                                {links.transmissionUrl && (
                                  <Button
                                    component="a"
                                    href={links.transmissionUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="sm"
                                    variant="solid"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    Reply
                                  </Button>
                                )}
                                {links.documentUrl && (
                                  <Button
                                    component="a"
                                    href={links.documentUrl}
                                    size="sm"
                                    variant="soft"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    Document
                                  </Button>
                                )}
                              </>
                            );
                          })()}
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </Card>

      {/* Preview Modal */}
      <Modal open={showPreview} onClose={closePreview}>
        <ModalDialog size="lg">
          <DialogTitle>Email Preview</DialogTitle>
          <DialogContent>
            {selectedLog && (
              <Stack spacing={2}>
                <Box>
                  <Typography level="title-sm">Template Type</Typography>
                  <Typography level="body-sm">{selectedLog.template_type}</Typography>
                </Box>
                <Box>
                  <Typography level="title-sm">Recipient</Typography>
                  <Typography level="body-sm">{selectedLog.recipient}</Typography>
                </Box>
                <Box>
                  <Typography level="title-sm">Status</Typography>
                  <Chip
                    size="sm"
                    color={getStatusColorForLog(selectedLog)}
                    variant="soft"
                  >
                    {getStatusLabel(selectedLog)}
                  </Chip>
                </Box>
                {(() => {
                  const details = getCustomerActionDetails(selectedLog);
                  const action = String(details.action || '').trim().toLowerCase();
                  if (action !== 'reject' && action !== 'rejected') return null;
                  const reason = String(details.reason || '').trim();
                  if (!reason) return null;
                  return (
                    <Box>
                      <Typography level="title-sm">Rejection reason</Typography>
                      <Typography level="body-sm">{reason}</Typography>
                    </Box>
                  );
                })()}
                {(() => {
                  const details = getCustomerActionDetails(selectedLog);
                  const action = String(details.action || '').trim().toLowerCase();
                  if (action !== 'query') return null;
                  const message = String(details.message || '').trim();
                  if (!message) return null;
                  return (
                    <Box>
                      <Typography level="title-sm">Customer query</Typography>
                      <Typography level="body-sm">{message}</Typography>
                    </Box>
                  );
                })()}
                {(() => {
                  const { openedAt, openCount, internalOpenCount } = getOpenMeta(selectedLog);
                  if (!openedAt && !internalOpenCount) return null;
                  return (
                    <Box>
                      <Typography level="title-sm">Opens</Typography>
                      <Typography level="body-sm">
                        {openedAt ? `Customer opened: ${formatDate(openedAt)}` : 'Customer opened: —'}
                        {typeof openCount === 'number' ? ` (count: ${openCount})` : ''}
                        {typeof internalOpenCount === 'number' ? ` · Staff previews: ${internalOpenCount}` : ''}
                      </Typography>
                    </Box>
                  );
                })()}
                <Box>
                  <Typography level="title-sm">Sent At</Typography>
                  <Typography level="body-sm">{formatDate(selectedLog.created_at)}</Typography>
                </Box>
                {selectedLog.error_message && (
                  <Box>
                    <Typography level="title-sm" color="danger">Error Message</Typography>
                    <Typography level="body-sm" color="danger">
                      {selectedLog.error_message}
                    </Typography>
                  </Box>
                )}
                {(() => {
                  const actionType = getCustomerActionType(selectedLog);
                  if (!['accept', 'reject', 'query'].includes(actionType)) return null;
                  const links = getTransmissionAndDocumentLinks(selectedLog, linkMetaMap);
                  if (!links.transmissionUrl && !links.documentUrl) return null;
                  return (
                    <Box>
                      <Typography level="title-sm">Related links</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        {links.transmissionUrl && (
                          <Button
                            component="a"
                            href={links.transmissionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            variant="outlined"
                          >
                            Public Doc
                          </Button>
                        )}
                        {links.transmissionUrl && (
                          <Button
                            component="a"
                            href={links.transmissionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            variant="solid"
                          >
                            Reply
                          </Button>
                        )}
                        {links.documentUrl && (
                          <Button
                            component="a"
                            href={links.documentUrl}
                            size="sm"
                            variant="outlined"
                          >
                            Document
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  );
                })()}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closePreview}>
              Close
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="outlined"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Typography level="body-sm" sx={{ alignSelf: 'center', px: 1 }}>
              Page {page} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
            </Typography>
            <Button
              size="sm"
              variant="outlined"
              disabled={page * PAGE_SIZE >= totalCount}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default EmailActivityDashboard; 